import { RefObject, useEffect, useMemo } from 'react'
import type { MenuProps } from '@mui/material/Menu'

export type CloseOnPointerDownOutsideOptions = {
    /** Menu/popover paper element — clicks inside do not close. */
    containerRef: RefObject<HTMLElement | null>
    /** Extra elements to treat as inside (e.g. anchor button when using anchorPosition). */
    ignoreRefs?: RefObject<HTMLElement | null>[]
    /** Extra CSS selectors; clicks matching `closest(selector)` do not close. */
    ignoreSelectors?: string[]
}

/**
 * Closes on pointer down outside the menu paper and optional ignore targets.
 * Use with non-modal menus (hideBackdrop + pointer-events passthrough on the root).
 */
export function useCloseOnPointerDownOutside(
    open: boolean,
    onClose: () => void,
    { containerRef, ignoreRefs = [], ignoreSelectors = [] }: CloseOnPointerDownOutsideOptions
) {
    useEffect(() => {
        if (!open) return

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null
            if (!target) return
            if (containerRef.current?.contains(target)) return

            for (const ref of ignoreRefs) {
                if (ref.current?.contains(target)) return
            }

            if (target instanceof Element) {
                for (const selector of ignoreSelectors) {
                    if (target.closest(selector)) return
                }
            }

            onClose()
        }

        document.addEventListener('pointerdown', handlePointerDown, true)
        return () => document.removeEventListener('pointerdown', handlePointerDown, true)
    }, [open, onClose, containerRef, ignoreRefs, ignoreSelectors])
}

/**
 * Second click on the anchor / trigger button closes the menu (toggle off).
 * Runs in capture phase so parent open handlers do not re-open on the same click.
 */
export function useToggleCloseOnAnchorClick(
    open: boolean,
    onClose: MenuProps['onClose'],
    toggleRefs: RefObject<HTMLElement | null>[],
    disabled = false
) {
    const toggleRefKeys = useMemo(
        () => toggleRefs.map((ref) => ref.current).join('|'),
        [toggleRefs]
    )

    useEffect(() => {
        if (!open || disabled || !onClose) return

        const handleClickCapture = (event: MouseEvent) => {
            const target = event.target as Node | null
            if (!target) return

            for (const ref of toggleRefs) {
                if (ref.current?.contains(target)) {
                    onClose(event, 'backdropClick')
                    event.stopImmediatePropagation()
                    return
                }
            }
        }

        document.addEventListener('click', handleClickCapture, true)
        return () => document.removeEventListener('click', handleClickCapture, true)
    }, [open, onClose, disabled, toggleRefs, toggleRefKeys])
}
